/**
 * 基于 flutter_inappwebview 的可靠 Bridge 方案
 * 
 * 就绪检测优先级：
 * 1. flutterInAppWebViewPlatformReady 事件：插件原生事件，handler 注册完即触发（最早、最可靠）
 * 2. flutterReady 自定义事件：Flutter 端 onLoadStop 注入 bridge 脚本后派发（较晚，作为兜底）
 * 3. 标记检测：检测 window.flutter_inappwebview / window._flutterReady / window.AppInterface
 * 4. 超时兜底：超时后抛异常，触发上层降级
 */

let flutterReady = false;
const readyCallbacks: Array<() => void> = [];

/**
 * 标记 bridge 已就绪，触发所有等待中的回调
 */
function markBridgeReady() {
  if (flutterReady) return; // 防止重复触发
  flutterReady = true;
  console.log('[FlutterBridge] Bridge 已就绪，触发', readyCallbacks.length, '个等待回调');
  readyCallbacks.forEach(cb => cb());
  readyCallbacks.length = 0;
}

// ==================== 事件监听（核心就绪机制） ====================
if (typeof window !== 'undefined') {
  // 1. ⭐ 最重要：flutter_inappwebview 插件原生就绪事件
  //    在 onWebViewCreated 注册完 handler 后自动触发，远早于 onLoadStop
  window.addEventListener('flutterInAppWebViewPlatformReady', () => {
    console.log('[FlutterBridge] 收到 flutterInAppWebViewPlatformReady 事件（插件原生）');
    markBridgeReady();
  });

  // 2. 自定义 flutterReady 事件（onLoadStop 注入 bridge 脚本后派发，作为兜底）
  window.addEventListener('flutterReady', () => {
    console.log('[FlutterBridge] 收到 flutterReady 事件');
    markBridgeReady();
  });

  // 3. 兜底：Flutter 端可能直接调用 window.onFlutterReady()
  // @ts-ignore
  window.onFlutterReady = () => {
    console.log('[FlutterBridge] 收到 onFlutterReady 调用');
    markBridgeReady();
  };

  // 4. 启动时立即检测一次（事件可能在模块加载前已触发过）
  // @ts-ignore
  if (window.flutter_inappwebview || window._flutterReady || window.AppInterface) {
    console.log('[FlutterBridge] 启动时检测到 bridge 已存在');
    markBridgeReady();
  }
}

/**
 * 判断是否在 App 环境中 (基于项目中从 URL 获取 from=app 标示的校验方式)
 */
const isInApp = (): boolean => {
  if (typeof window === 'undefined') return false
  // 主判断：URL 参数 from=app
  const params = new URLSearchParams(window.location.search)
  if (params.get('from') === 'app') return true
  // 备用判断：window.AppInterface 注入（兼容旧逻辑）
  return !!window.AppInterface
};

/**
 * 封装好的给业务使用的通信实体
 */
export const FlutterBridge = {
  /**
   * 安全调用 Flutter 方法
   * @param method 约定好的方法名称
   * @param args 要传递的参数（最后一个参数可以是 { _timeout?: number } 选项对象）
   * @returns Promise 返回 Flutter 异步返回的结果
   */
  async call(method: string, ...args: any[]): Promise<any> {
    // 支持在最后一个参数传入 { _timeout: number } 来自定义超时（毫秒）
    let timeoutMs = 3000;
    const lastArg = args[args.length - 1];
    if (lastArg && typeof lastArg === 'object' && '_timeout' in lastArg) {
      timeoutMs = (lastArg as any)._timeout;
      args = args.slice(0, -1);
    }
    // 如果不是从 from=app 的标志位进来的，直接报错抛转给上层兜底
    if (!isInApp()) {
      console.warn(`[FlutterBridge] 未检测到 from=app 标志，可能是不支持新频道的旧版 App，抛出异常以触发降级: ${method}`);
      throw new Error('[FlutterBridge] 不在新版 App 环境中');
    }

    // ⭐ 等待 bridge 就绪（事件驱动 + 超时兜底）
    if (!flutterReady) {
      // 同步检测一次（可能 flutterInAppWebViewPlatformReady 已经触发过了）
      // @ts-ignore
      if (window.flutter_inappwebview || window._flutterReady || window.AppInterface) {
        markBridgeReady();
      } else {
        // 挂到事件回调队列，等 flutterInAppWebViewPlatformReady 或 flutterReady 事件触发
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(() => {
            // @ts-ignore
            if (window.flutter_inappwebview || window._flutterReady || window.AppInterface) {
              markBridgeReady();
              return resolve();
            }
            reject(new Error('[FlutterBridge] 等待 Flutter Bridge 就绪超时'));
          }, 3000);

          readyCallbacks.push(() => {
            clearTimeout(timer);
            resolve();
          });
        });
      }
    }

    // @ts-ignore
    // if (!window.flutter_inappwebview || typeof window.flutter_inappwebview.callHandler !== 'function') {
    //   throw new Error('[FlutterBridge] flutter_inappwebview 通道不可用');
    // }

    // 执行调用并加入超时机制（超时后抛出异常，让外部走兜底）
    // @ts-ignore
    const callPromise = window.flutter_inappwebview.callHandler(method, ...args);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`[FlutterBridge] callHandler('${method}') timeout`)), timeoutMs);
    });

    return Promise.race([callPromise, timeoutPromise]);
  }
};

export default FlutterBridge;
